import { Input } from "@norican/react";
import { useState } from "react";

import { subText } from "@/components/table";
import type { OrgPerson } from "@/lib/api";

/**
 * Pick somebody out of the org snapshot.
 *
 * Deliberately not `PersonPicker`: that one searches the live directory for
 * an offboarding, this one searches the frozen snapshot a scenario or an
 * ad-hoc change is built against, because a person who is not in the baseline
 * cannot be the subject of a change to it.
 */
export function PersonSearch({
  people,
  label,
  value,
  onChange,
}: {
  people: OrgPerson[];
  label: string;
  value: OrgPerson | null;
  onChange: (p: OrgPerson | null) => void;
}) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const hits =
    q.length < 2
      ? []
      : people
          .filter(
            (p) =>
              p.account_enabled !== false &&
              ((p.display_name ?? "").toLowerCase().includes(q) ||
                (p.upn ?? "").toLowerCase().includes(q)),
          )
          // People before admin, mailbox and service accounts: "Wood, Carl"
          // also exists as "Wood, Carl (admin)", and picking that as a manager
          // is an easy, silent mistake.
          .sort((a, b) => Number(a.person === false) - Number(b.person === false))
          .slice(0, 8);
  return (
    <label className="text-fg-muted relative flex flex-col text-xs">
      {label}
      {value ? (
        <span className="text-fg flex items-center gap-2 py-1 text-sm">
          {value.display_name}
          <button className="text-fg-muted text-xs underline" onClick={() => onChange(null)}>
            change
          </button>
        </span>
      ) : (
        <Input
          placeholder="Type a name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}
      {!value && hits.length ? (
        <ul className="border-border bg-bg absolute top-full z-10 mt-1 w-96 rounded border shadow">
          {hits.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="hover:bg-bg-subtle w-full px-3 py-1 text-left text-sm"
                onClick={() => {
                  onChange(p);
                  setQuery("");
                }}
              >
                {p.display_name}
                <span className={`${subText} ml-2`}>
                  {p.person === false ? "account, not a person · " : ""}
                  {p.upn}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </label>
  );
}
